import { Component, OnInit, Output, EventEmitter, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CellInformationService } from '@services/shared/cell-information/cell-information.service';
import { AutoUnsubscribe } from '../../../decorators/auto-unsubscribe';
import { Subscription } from 'rxjs';
import { DataFrameValue } from '@models/dataframe';
import { SourceComment } from '@components/main/spreading/models/source-comment';
import { DataView } from '@models/dataview';
import { DataViewColumn } from '@models/dataview';

@Component({
    selector: 'app-comment-sidebar',
    templateUrl: './comment-sidebar.component.html',
    styleUrls: ['./comment-sidebar.component.scss'],
    standalone: false
})
@AutoUnsubscribe('subscriptionArray$')
export class CommentSidebarComponent implements OnInit, OnChanges {

  @Output() closeCommentView = new EventEmitter<void>();
  @Input() activeDataView: DataView;
  @Input() selectedColumn: DataViewColumn;
  
  cellInformation: DataFrameValue;
  cellInformationTitle: string;
  cellInformationStatementDate: string;
  cellInformationVisible: boolean;
  subscriptionArray$: Subscription[] = [];

  constructor(
    private cellInformationService: CellInformationService
  ) { }

  ngOnInit(): void {
    this.subscriptionArray$.push(
      this.cellInformationService.cellInformationVisible$
        .subscribe(cellInformationVisible => this.cellInformationVisible = cellInformationVisible)
    );

    this.subscriptionArray$.push(
      this.cellInformationService.cellInformationTitle$
        .subscribe(cellInformationTitle => this.cellInformationTitle = cellInformationTitle)
    );

    this.subscriptionArray$.push(
      this.cellInformationService.cellInformation$
        .subscribe(cellInformation => this.cellInformation = cellInformation)
    );

    this.subscriptionArray$.push(
      this.cellInformationService.cellInformationStatementDate$
        .subscribe(cellInformationStatementDate =>
          this.cellInformationStatementDate = cellInformationStatementDate
        )
    );

    // Set up cell information for column comments if selectedColumn is provided
    this.setupCellInformationForColumn();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If selectedColumn changes, update the cell information
    if (changes['selectedColumn'] && !changes['selectedColumn'].firstChange) {
      this.setupCellInformationForColumn();
    }
  }

  private setupCellInformationForColumn(): void {
    if (this.selectedColumn && this.selectedColumn.sourceComments && this.selectedColumn.sourceComments.length > 0) {
      // Create cell information for the selected column
      const cellInfo = new DataFrameValue();
      cellInfo.sourceComments = this.selectedColumn.sourceComments;
      cellInfo.lineItem = null;
      cellInfo.value = 0;
      cellInfo.originalValue = 0;
      cellInfo.isHeader = false;
      cellInfo.requiredValue = 0;
      
      // Set the cell information to display column comments
      this.cellInformation = cellInfo;
      this.cellInformationTitle = null;
      this.cellInformationStatementDate = this.selectedColumn.statementDate;
    }
  }

  hideCellInformation(): void {
    this.cellInformationService.hideCellInformation();
    this.closeCommentView.emit();
  }

  hasComments(): boolean {
    return !!this.cellInformation?.sourceComments?.length;
  }

  getTitle(sourceComment: SourceComment): string {
    return sourceComment.lineItemLabel || this.cellInformationTitle || sourceComment.stmtName || 'Unknown';
  }

  getCommentTimestamp(sourceComment: SourceComment): string {
    const utcTimestamp = sourceComment?.changedDate || sourceComment?.createdDate;

    if (!utcTimestamp) {
      return 'Unknown';
    }

    try {
      const date = new Date(utcTimestamp);

      if (isNaN(date.getTime())) {
        return 'Unknown';
      }

      const timeOptions: Intl.DateTimeFormatOptions = {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      };
      const timeString = date.toLocaleTimeString('en-US', timeOptions);

      const dateOptions: Intl.DateTimeFormatOptions = {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      };
      const dateString = date.toLocaleDateString('en-US', dateOptions);

      const timeZone = date.toLocaleDateString('en-US', { timeZoneName: 'short' })
        .split(', ')[1] || 'EST';

      if (sourceComment?.changedDate){
        return `Edited: ${timeString} ${dateString} ${timeZone}`;
      }
      return `${timeString} ${dateString} ${timeZone}`;

    } catch (error) {
      return 'Unknown';
    }
  }

  getCommentAuthor(sourceComment: SourceComment): string {
    return sourceComment.createdBy || 'Unknown';
  }
}
